import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../utils/supabaseClient';
import axios from 'axios';

// IMPORTANT:
// - In production you should set REACT_APP_API_BASE_URL to your API origin + "/api"
//   e.g. "https://www.api.tryzilch.com/api"
// - Never default to a placeholder domain in prod, or auth init will silently hang.
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '/api';

// Cold-start tolerance. The staging API runs on a free Render tier that sleeps
// when idle and can take ~30s to answer its first request. A short timeout here
// leaves the user authenticated with Supabase but with no supplierInfo, which is
// indistinguishable from a failed login. Override per-deployment if a tighter
// bound is wanted.
const API_TIMEOUT_MS = Number(process.env.REACT_APP_API_TIMEOUT_MS) || 30000;

// A sleeping backend often rejects the very first request outright (502/connection
// reset) while it boots, then serves the retry fine. One retry covers that.
const isColdStartError = (error) =>
  error.code === 'ECONNABORTED' ||
  error.code === 'ERR_NETWORK' ||
  [502, 503, 504].includes(error.response?.status);

const getWithColdStartRetry = async (url) => {
  try {
    return await axios.get(url, { timeout: API_TIMEOUT_MS });
  } catch (error) {
    if (!isColdStartError(error)) throw error;
    console.warn(`[AuthContext] ${url} failed to respond (${error.message}); retrying once`);
    return axios.get(url, { timeout: API_TIMEOUT_MS });
  }
};

export const AuthContext = createContext({});

// The staff flag from the last /auth/me, read synchronously so staff-only UI
// renders on the first paint of later loads. Prefer `isStaff` once
// supplierInfo has loaded; it corrects this if it ever drifts.
export function readCachedIsStaff() {
  try {
    return typeof window !== 'undefined' && localStorage.getItem('zilch_is_staff') === '1';
  } catch (_) {
    return false;
  }
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [supplierInfo, setSupplierInfo] = useState(null);
  const [billing, setBilling] = useState(null);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const fetchingSupplierInfo = React.useRef(false);
  const fetchingBillingInfo = React.useRef(false);

  // Set up axios interceptor to add auth token to requests
  useEffect(() => {
    const requestInterceptor = axios.interceptors.request.use(
      async (config) => {
        // Only add token to requests to our API
        const url = config.url || '';
        const isApiRequest = url.startsWith('/api') || 
                           url.includes('/api/') || 
                           url.includes(API_BASE_URL) ||
                           (API_BASE_URL !== '/api' && url.startsWith(API_BASE_URL));
        
        if (isApiRequest) {
          try {
            const { data: { session }, error } = await supabase.auth.getSession();
            if (error) {
              console.warn('[AuthContext] Error getting session in interceptor:', error);
            } else if (session?.access_token) {
              config.headers.Authorization = `Bearer ${session.access_token}`;
            } else {
              console.warn('[AuthContext] No session token available for request:', url);
            }

            // Staff org impersonation: when a target org is set, tell the API to
            // operate as that org. The server only honours this for is_staff users.
            // Read from localStorage because this interceptor runs outside React state.
            try {
              const impersonateOrg = localStorage.getItem('zilch_impersonate_org');
              if (impersonateOrg) {
                config.headers['X-Impersonate-Org'] = impersonateOrg;
              }
            } catch (_) { /* localStorage unavailable */ }
          } catch (err) {
            console.error('[AuthContext] Error in request interceptor:', err);
          }
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.request.eject(requestInterceptor);
    };
  }, []);

  // Fetch supplier info when user is authenticated
  const fetchSupplierInfo = async () => {
    // Prevent multiple simultaneous calls
    if (fetchingSupplierInfo.current) {
      return;
    }
    
    fetchingSupplierInfo.current = true;
    
    try {
      const response = await getWithColdStartRetry(`${API_BASE_URL}/auth/me`);
      if (response.data.success) {
        setSupplierInfo(response.data.data);
        // Cache is_staff so the Navbar can render the Admin item on the very
        // first paint of subsequent loads, before /auth/me resolves. This is
        // a UX hint only — the server is the source of truth for /admin.
        try {
          if (response.data.data?.is_staff === true) {
            localStorage.setItem('zilch_is_staff', '1');
          } else {
            localStorage.removeItem('zilch_is_staff');
          }
          // Same trick for the Settings sidebar: the Billing tab is admin-only,
          // so without a cached role it only appears once /auth/me lands — which
          // on a cold API can be several seconds after the page has painted.
          if (response.data.data?.role) {
            localStorage.setItem('zilch_role', response.data.data.role);
          } else {
            localStorage.removeItem('zilch_role');
          }
        } catch (_) { /* localStorage unavailable */ }
      }
    } catch (error) {
      console.error('[AuthContext] Failed to fetch supplier info:', error.message);
      
      // If timeout or connection refused, server is down
      if (error.code === 'ECONNABORTED' || error.code === 'ERR_NETWORK') {
        console.error('[AuthContext] Backend server is not responding!');
      }
      
      // If user isn't in supplier_users table, sign them out
      if (error.response?.status === 403 || error.response?.status === 401) {
        console.warn('[AuthContext] User not associated with supplier, signing out');
        await supabase.auth.signOut();
        setUser(null);
        setSession(null);
        setSupplierInfo(null);
      }
    } finally {
      fetchingSupplierInfo.current = false;
    }
  };

  // Fetch billing info when user is authenticated
  const fetchBillingInfo = async () => {
    if (fetchingBillingInfo.current) return;
    fetchingBillingInfo.current = true;
    try {
      const response = await getWithColdStartRetry(`${API_BASE_URL}/billing/status`);
      if (response.data?.success) {
        setBilling(response.data.data);
      }
    } catch (error) {
      console.error('[AuthContext] Failed to fetch billing info:', error.message);
      // Don't sign out on billing failures; treat as optional.
      setBilling(null);
    } finally {
      fetchingBillingInfo.current = false;
    }
  };

  // Initialize auth state
  useEffect(() => {
    let mounted = true;
    let timeoutId;
    
    const initAuth = async () => {
      try {
        
        // Safety timeout - avoids spinner-lock if something goes wrong.
        // Keep this fairly generous; auth + backend calls can take a few seconds on cold loads.
        timeoutId = setTimeout(() => {
          if (mounted) {
            console.warn('[AuthContext] Auth init timeout - forcing loading to false');
            setLoading(false);
          }
        }, 10000);

        // NOTE:
        // Do NOT hardcode Supabase localStorage keys. They change per project ref and env.
        // Always use the Supabase SDK as the source of truth.
        let session = null;
        try {
          // Avoid imposing an additional timeout here; Supabase may take longer on cold loads.
          // We already have a separate UI safety timeout above to prevent spinner-lock.
          const { data } = await supabase.auth.getSession();
          session = data?.session ?? null;
        } catch (err) {
          console.warn('[AuthContext] getSession failed:', err.message);
          session = null;
        }
        
        if (!mounted) return;
        
        setSession(session);
        setUser(session?.user ?? null);

        if (session?.user) {
          // Don't block initial auth hydration on backend calls.
          // If the API is slow/down, we still want the app to render and let the user retry.
          Promise.resolve()
            .then(() => fetchSupplierInfo())
            .catch((e) => console.warn('[AuthContext] fetchSupplierInfo failed:', e?.message || e));
          Promise.resolve()
            .then(() => fetchBillingInfo())
            .catch((e) => console.warn('[AuthContext] fetchBillingInfo failed:', e?.message || e));
        }
      } catch (error) {
        console.error('[AuthContext] Error initializing auth:', error);
        // On timeout or error, set empty session
        if (mounted) {
          setSession(null);
          setUser(null);
          setSupplierInfo(null);
          setBilling(null);
        }
      } finally {
        if (mounted) {
          clearTimeout(timeoutId);
          setLoading(false);
        }
      }
    };

    initAuth();

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (_event, session) => {
      if (!mounted) return;
      
      setSession(session);
      setUser(session?.user ?? null);

      if (session?.user) {
        // Same rule: don't block UI on backend calls.
        Promise.resolve()
          .then(() => fetchSupplierInfo())
          .catch((e) => console.warn('[AuthContext] fetchSupplierInfo failed:', e?.message || e));
        Promise.resolve()
          .then(() => fetchBillingInfo())
          .catch((e) => console.warn('[AuthContext] fetchBillingInfo failed:', e?.message || e));
      } else {
        setSupplierInfo(null);
        setBilling(null);
      }

      if (mounted) {
        setLoading(false);
      }
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  // Sign in with email and password
  const signIn = async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) throw error;

    if (data.user && data.session) {
      // Wait a bit for the session to be fully established
      await new Promise(resolve => setTimeout(resolve, 100));
      // Set the session state first
      setSession(data.session);
      setUser(data.user);
      // Then fetch supplier info
      await fetchSupplierInfo();
    }

    return data;
  };

  // Sign out
  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
    setUser(null);
    setSession(null);
    setSupplierInfo(null);
    setBilling(null);
    
    // Clear legacy localStorage
    localStorage.removeItem('supplierId');
    localStorage.removeItem('isLoggedIn');
    localStorage.removeItem('zilch_is_staff');
    localStorage.removeItem('zilch_role');
    localStorage.removeItem('zilch_impersonate_org');
  };

  // Change password
  const changePassword = async (newPassword) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/auth/change-password`, {
        new_password: newPassword
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  };

  // Get auth token
  const getToken = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    return session?.access_token;
  };

  const isStaff = supplierInfo?.is_staff === true;
  const isImpersonating = supplierInfo?.is_impersonating === true;
  const impersonatedOrgName = supplierInfo?.impersonated_org_name || null;

  // Staff: begin acting as another organisation. Persists the target org id so
  // the axios interceptor attaches the X-Impersonate-Org header, then refreshes
  // supplier/billing info so the whole app reflects the impersonated org.
  const startImpersonation = async (orgId) => {
    try {
      localStorage.setItem('zilch_impersonate_org', orgId);
    } catch (_) { /* localStorage unavailable */ }
    await fetchSupplierInfo();
    await fetchBillingInfo();
  };

  // Stop impersonating and return to the staff member's own org.
  const stopImpersonation = async () => {
    try {
      localStorage.removeItem('zilch_impersonate_org');
    } catch (_) { /* localStorage unavailable */ }
    await fetchSupplierInfo();
    await fetchBillingInfo();
  };

  const value = {
    user,
    session,
    supplierInfo,
    billing,
    loading,
    isStaff,
    isImpersonating,
    impersonatedOrgName,
    startImpersonation,
    stopImpersonation,
    signIn,
    signOut,
    changePassword,
    getToken,
    refreshSupplierInfo: fetchSupplierInfo,
    refreshBillingInfo: fetchBillingInfo,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

